/**
 * 챗봇 런처 버튼 — 우측 하단 60x60 원형 버튼
 *
 * 비로그인 상태에서만 pulse 애니메이션으로 시선 유도.
 * 로그인 상태에서는 사용자가 이미 서비스 사용 중이라 시선 유도 불필요 → pulse 제거.
 */

import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

interface LauncherProps {
  isOpen: boolean;
  onClick: () => void;
  hasNewMessage?: boolean;
  unreadCount?: number;
}

export function Launcher({
  isOpen,
  onClick,
  hasNewMessage = false,
  unreadCount,
}: LauncherProps) {
  const authStatus = useAuthStore((s) => s.status);
  const showPulse = !isOpen && authStatus !== 'authenticated';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isOpen ? '챗봇 닫기' : '챗봇 열기'}
      className={cn(
        'ml-fixed ml-right-7 ml-bottom-7 ml-z-cb-launcher',
        'ml-w-[60px] ml-h-[60px] ml-rounded-full',
        'ml-flex ml-items-center ml-justify-center',
        'ml-text-white ml-cursor-pointer',
        'ml-transition-all ml-duration-200',
        'ml-shadow-cb-launcher',
        'hover:ml-scale-105',
        isOpen ? 'ml-bg-ink ml-rotate-90' : 'ml-bg-brand-600 hover:ml-bg-brand-500',
        // pulse ring — 닫혀 있고 비로그인일 때만
        showPulse &&
          'before:ml-content-[""] before:ml-absolute before:ml-inset-0 before:ml-rounded-full before:ml-border-2 before:ml-border-brand-600 before:ml-animate-pulse-ring before:ml-pointer-events-none'
      )}
    >
      {isOpen ? <CloseIcon /> : <ChatIcon />}
      {!isOpen && hasNewMessage && (
        <span
          className={cn(
            'ml-absolute ml-top-0 ml-right-0',
            'ml-min-w-[20px] ml-h-[20px] ml-px-1.5',
            'ml-bg-accent ml-text-white ml-rounded-full',
            'ml-text-[11px] ml-font-bold',
            'ml-flex ml-items-center ml-justify-center',
            'ml-border-2 ml-border-white'
          )}
        >
          {unreadCount !== undefined && unreadCount > 0 ? unreadCount : '!'}
        </span>
      )}
    </button>
  );
}

function ChatIcon() {
  return (
    <svg
      className="ml-w-[26px] ml-h-[26px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      className="ml-w-[26px] ml-h-[26px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
